import clsx from "clsx";
import { type ComponentProps, FunctionComponent, useRef } from "react";
import { graphql, useFragment } from "react-relay";

import GridSlide from "@story/data-centers-hub/components/GridSlide";
import TitleCellMedia from "@story/data-centers-hub/components/slides/TitleCellMedia";
import {
  loudounCopy,
  loudounSlideMedia,
} from "@story/data-centers-hub/config/loudounAssets";
import { resolveLockupCopy } from "@story/data-centers-hub/config/lockupCopy";
import { wipeClipAnimAttrs } from "@story/data-centers-hub/config/wipeClipAnimAttrs";
import { loudounCells } from "@story/data-centers-hub/config/slides";
import { asStill } from "@story/data-centers-hub/config/titleAssets";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { useStageBottomClearance } from "@story/data-centers-hub/hooks/useStageBottomClearance";
import { useWipeChapterStage } from "@story/data-centers-hub/hooks/useWipeChapterStage";
import getBrowserInfo from "src/utils/get-browser-info";
import ShareButtons from "@voxmedia/duet-ui/components/article/ShareButtons";
import shareButtonsThevergeConfig from "@voxmedia/duet-ui/components/article/ShareButtons/config/ShareButtons.theverge.config";
import CommentCount from "@voxmedia/duet-ui/components/meta/CommentCount";
import { LoudounSlideMeta_feedContentResource$key } from "src/__generated__/relay/LoudounSlideMeta_feedContentResource.graphql";
import { useCskCommentCount } from "src/hooks/use-csk-comment-count";
import { useCskCommentsLinkUrl } from "src/hooks/use-csk-comments-link-url";
import { useCskCoralStoryId } from "src/hooks/use-csk-coral-story-id";
import { useShowStoryComments } from "src/hooks/use-show-story-comments";

import * as styles from "./LoudounSlide.css";

interface Props {
  /** Chapter is the active slide. */
  active: boolean;
  /**
   * When true, play the fixed-duration enter/exit wipe. When false (motion off /
   * reduced-motion), snap straight to settled or hidden.
   */
  animate: boolean;
  /**
   * Stay fully open while a later chapter fades in. The video keeps playing
   * until this drops, then the stage closes without the title-bound exit wipe.
   */
  hold?: boolean;
  /** Play the exit wipe. Only when leaving back to the title. */
  depart?: boolean;
  /** Forward wipe in progress — keep media playing until it settles. */
  leaving?: boolean;
  /** Mount already open — session restore / soft remount (skip enter wipe). */
  resume?: boolean;
  /** Hide after the incoming wipe ends — blocks Android underlay flash. */
  muted?: boolean;
  /** Attach video `src` when this chapter is in the warm neighborhood. */
  mediaWarm?: boolean;
  featureHref?: string | null;
  /** Loudoun feature node for share / comment meta. */
  loudounMeta?: LoudounSlideMeta_feedContentResource$key | null;
}

/**
 * Loudoun chapter: left photo + byline plate, BR still, full-stage video, dek
 * under the nav, hed over the stage. Narrow: video top-half (bottom→up), left
 * inset image only (top→down), bottom-half BR still + dek/meta (right→left).
 * Narrow wipe: hed clones inside each cell. Settled: one high-z `hedBleed`
 * above cell rules (same frame — no hairline through the type).
 */
const LoudounSlide: FunctionComponent<Props> = ({
  active,
  animate,
  hold = false,
  depart = false,
  leaving = false,
  resume = false,
  muted = false,
  mediaWarm = true,
  featureHref,
  loudounMeta = null,
}) => {
  const narrow = useHubNarrowViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const plateShellRef = useRef<HTMLDivElement>(null);

  // Plate is content-sized. The lockup pads by this clearance plus the
  // shared hed/dek gap, measured before paint so it doesn't jump.
  useStageBottomClearance(
    rootRef,
    plateShellRef,
    "--loudoun-plate-h",
    narrow,
  );
  const data = useFragment(
    graphql`
      fragment LoudounSlideMeta_feedContentResource on FeedContentResourceInterface {
        authors {
          name
        }
        additionalBylines
        dek {
          plaintext
        }
        title
        id
        globalId
        chorusId
        chorusUuid
        resourceType
        permalink
        comments {
          enabled
          mode
        }
      }
    `,
    loudounMeta,
  );

  const {
    open,
    settled,
    fillPin,
    fillLock,
    departGone,
    painted,
    mediaActive,
    mediaPlayback,
    stillsActive,
  } = useWipeChapterStage({
    active,
    animate,
    hold,
    depart,
    leaving,
    resume,
    mediaWarm,
    narrow,
  });

  const lockup = resolveLockupCopy(data, loudounCopy);
  const permalink = data?.permalink ?? featureHref ?? "";
  const title = data?.title ?? loudounCopy.shareTitle;
  const coralId = useCskCoralStoryId({
    chorusId: data?.chorusId,
    chorusUuid: data?.chorusUuid,
    globalId: data?.globalId,
    nodeId: data?.id,
  });
  const showComments = useShowStoryComments(data?.comments ?? null);
  // Nav / share chrome follows the in-view chapter only — not hold/open paint.
  const loudounChrome = active;
  // Share/comments stay mounted for the life of this slide. Gating them on
  // stage paint remounts CommentCount when the wipe starts, which replays its
  // entrance animation.
  const showMeta = Boolean(permalink);
  useCskCommentCount(showComments && data ? coralId : undefined);
  const commentsLinkUrl = useCskCommentsLinkUrl(permalink);

  const articleHref = featureHref ?? (permalink || null);

  const hed = (
    <div className={styles.hed}>
      <img
        className={styles.hedImg}
        src={loudounSlideMedia.hed}
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="eager"
        fetchPriority={loudounChrome ? "high" : "low"}
      />
      <h2 className="sr-only">{loudounCopy.hedAlt}</h2>
    </div>
  );

  const hedBleed = (offsetClass?: string) => (
    <div className={clsx(styles.hedBleed, offsetClass)} aria-hidden="true">
      <img
        className={styles.hedImg}
        src={loudounSlideMedia.hedMobile}
        alt=""
        decoding="async"
        loading="eager"
        fetchPriority={loudounChrome ? "high" : "low"}
      />
    </div>
  );

  /** Wipe: in-cell clones. Settled / motion-off: one hed above the rules. */
  const showWipeHed = narrow && animate && !settled;
  const showSettledHed = narrow && (!animate || settled);

  const videoFrom = narrow ? styles.fromBottom : styles.fromCenter;
  const plateFrom = narrow ? styles.fromRight : styles.fromTop;
  const clipAnim = wipeClipAnimAttrs({
    animate,
    resume,
    active,
    hold,
    depart,
    open,
    fillPin,
    fillLock,
    departGone,
  });
  // Android keyframes need `from*` + `open` together; desktop keeps the
  // mutually exclusive swap so clip-path transitions still interpolate.
  const clip = (fromClass: string) =>
    getBrowserInfo()?.android
      ? clsx(fromClass, open && styles.open)
      : clsx(open ? styles.open : fromClass);
  const mosaicCells = narrow
    ? loudounCells.filter((cell) => cell.id !== "br")
    : loudounCells;

  const bylinePlate = (
    <div className={clsx(styles.bylineFore, clip(plateFrom))}>
      <div className={styles.bylinePlate}>
        {narrow ? <p className={styles.dek}>{lockup.lede}</p> : null}
        <div className={styles.bylines}>
          <p className={styles.byline}>
            {lockup.bylineLabel}{" "}
            <strong className={styles.bylineName}>{lockup.bylineName}</strong>
          </p>
          <p className={styles.byline}>
            {lockup.photoBylineLabel}{" "}
            <strong className={styles.bylineName}>
              {lockup.photoBylineName}
            </strong>
          </p>
        </div>
        {showMeta ? (
          <div className={styles.meta}>
            <ShareButtons
              {...(shareButtonsThevergeConfig as ComponentProps<
                typeof ShareButtons
              >)}
              url={permalink}
              title={title}
              layout="standard"
            />
            {showComments && data && coralId ? (
              <CommentCount
                coralId={coralId}
                url={commentsLinkUrl}
                authorName={data.authors?.[0]?.name ?? ""}
                contentType={data.resourceType ?? ""}
                headline={title}
                variant="lede"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  const panel = (
    <div
      ref={rootRef}
      className={clsx(
        styles.root,
        painted && !muted && styles.rootActive,
        muted && styles.rootMuted,
        settled && styles.rootSettled,
        !animate && styles.ruled,
      )}
      data-loudoun-open={loudounChrome ? "" : undefined}
      aria-hidden={active && settled ? undefined : true}
      {...clipAnim}
    >
      <div
        className={clsx(styles.videoUnderlay, clip(videoFrom))}
        aria-hidden="true"
      >
        <TitleCellMedia
          // Narrow: poster only — chapter loops + title mosaic crashed iOS.
          media={
            narrow
              ? asStill(loudounSlideMedia.video)
              : loudounSlideMedia.video
          }
          active={mediaActive}
          // Keep src on hold underlays; play only while on-stage / wiping.
          playback={mediaPlayback}
        />
        {showWipeHed ? hedBleed(styles.hedBleedVideo) : null}
      </div>
      {narrow ? (
        <div
          className={clsx(
            styles.videoLead,
            styles.leadFromBottom,
            open && styles.leadFromBottomOpen,
          )}
          aria-hidden="true"
        />
      ) : null}

      {narrow ? (
        <div
          className={clsx(
            styles.bottomStill,
            clip(styles.fromRight),
          )}
          aria-hidden="true"
        >
          <TitleCellMedia
            media={loudounSlideMedia.br}
            active={stillsActive}
          />
          {showWipeHed ? hedBleed(styles.hedBleedStill) : null}
        </div>
      ) : null}
      {narrow ? (
        <div
          className={clsx(
            styles.bottomStillLead,
            styles.leadFromRight,
            open && styles.leadFromRightOpen,
          )}
          aria-hidden="true"
        />
      ) : null}

      {animate && !narrow ? (
        <div
          className={clsx(styles.frame, open && styles.frameOpen)}
          aria-hidden="true"
        />
      ) : null}

      <GridSlide
        cells={mosaicCells}
        className={styles.grid}
        aria-label="Loudoun County slide"
        aria-hidden={active && settled ? undefined : true}
        renderCell={(cell) => {
          if (cell.id === "left") {
            return (
              <div
                className={clsx(
                  styles.revealShell,
                  styles.leadFromTop,
                  open && styles.leadFromTopOpen,
                )}
              >
                <div
                  className={clsx(styles.reveal, clip(styles.fromTop))}
                >
                  <TitleCellMedia
                    media={loudounSlideMedia.left}
                    active={stillsActive}
                  />
                  {showWipeHed ? hedBleed(styles.hedBleedVideo) : null}
                </div>
              </div>
            );
          }
          if (cell.id === "br") {
            return (
              <div
                className={clsx(
                  styles.revealShell,
                  styles.leadFromRight,
                  open && styles.leadFromRightOpen,
                )}
              >
                <div
                  className={clsx(styles.reveal, clip(styles.fromRight))}
                >
                  <TitleCellMedia
                    media={loudounSlideMedia.br}
                    active={stillsActive}
                  />
                </div>
              </div>
            );
          }
          return null;
        }}
        overlay={
          <div
            className={clsx(styles.hedLayer, clip(styles.fromCenter))}
          >
            {narrow ? (
              <h2 className="sr-only">{loudounCopy.hedAlt}</h2>
            ) : null}
            {articleHref ? (
              <a className={styles.slideLink} href={articleHref}>
                {narrow ? null : hed}
              </a>
            ) : narrow ? null : (
              hed
            )}
            {!narrow ? <p className={styles.dek}>{lockup.lede}</p> : null}
          </div>
        }
        forelayer={
          narrow ? (
            <div ref={plateShellRef} className={styles.plateShell}>
              <div
                className={clsx(
                  styles.bottomLead,
                  styles.leadFromRight,
                  open && styles.leadFromRightOpen,
                )}
                aria-hidden="true"
              />
              {bylinePlate}
            </div>
          ) : (
            bylinePlate
          )
        }
      />
      {showSettledHed ? (
        <div className={styles.hedClip}>
          <div className={styles.hedSettledMount}>
            {hedBleed(styles.hedBleedVideo)}
          </div>
        </div>
      ) : null}
    </div>
  );

  return panel;
};

export default LoudounSlide;
